import React, { type ReactNode } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import type { PreloadedEntryPoint } from 'react-relay';

import noop from '@atlaskit/ds-lib/noop';

type WaitForEntryPointLoadProps = {
	/**
	 * The preloaded entry point reference. Reading `getComponent()` will throw
	 * the loading promise (per react-relay's `loadEntryPoint`) until the entry
	 * point's module has finished loading. When wrapped in a `<Suspense>`
	 * boundary this means children will not mount until the entry point is
	 * ready to render.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: PreloadedEntryPoint<any>;
	children: ReactNode;
};

function SuspendUntilLoaded({
	entryPointReference,
	children,
}: WaitForEntryPointLoadProps): ReactNode {
	// The result is intentionally unused — this call exists purely to gate
	// the mount of children on the load completing.
	entryPointReference.getComponent();
	return children;
}

const renderNothing = () => null;

/**
 * Delays rendering children until the provided entry point module has
 * finished loading.
 *
 * The component manages its own Suspense + error boundary so it can be
 * dropped in alongside an entry point container without interfering with
 * the loading fallback or the container's existing error handling. Errors
 * from a failed entry-point load are already reported and handled by
 * `InternalEntryPointContainer`'s own ErrorBoundary, so the boundary here
 * is intentionally silent.
 */
export function WaitForEntryPointLoad({
	entryPointReference,
	children,
}: WaitForEntryPointLoadProps): React.JSX.Element {
	return (
		<ErrorBoundary FallbackComponent={renderNothing} onError={noop}>
			<React.Suspense fallback={null}>
				<SuspendUntilLoaded entryPointReference={entryPointReference}>
					{children}
				</SuspendUntilLoaded>
			</React.Suspense>
		</ErrorBoundary>
	);
}
